import * as amplitude from '@amplitude/analytics-browser';
import { useEffect, useMemo, useRef } from 'react';

import { useAuth } from 'modules/auth/ui/hooks/use-auth';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';

import { AnalyticsIdentity, buildAnalyticsIdentity } from './analytics-identity';
import { trackApplicationOpenedOnce } from './track-application-opened';

/**
 * Идентифицирует пользователя и его организацию в Amplitude. Вызывается один раз — в
 * `AnalyticsIdentityTracker` над роутером (`src/App.tsx`), чтобы того же пользователя видели и
 * экраны вне шелла: редакторы, выбор организатора, заглушка телефона.
 *
 * Почему хук, а не thunk `authCheck`: идентичность собирается из двух слайсов (`auth` и
 * `organizer`), которые наполняются независимо, и восстанавливается при F5 без повторного
 * входа. Плюс вызовы SDK — сайд-эффект UI, в `store/` им нельзя (`.claude/rules/architecture.md`).
 *
 * Сброс — только на выходе, при переходе `user: есть → null`: `amplitude.reset()` отвязывает
 * `device_id` от ушедшего пользователя. Не на размонтировании: пока хук жил в шелле, уход в
 * полноэкранный редактор сбрасывал пользователя и заводил новое устройство (IMGM-1609).
 */
export function useAnalyticsIdentity(): void {
  const { user, userTournamentId } = useAuth();
  const { info } = useOrganizer();

  const identity = useMemo(() => buildAnalyticsIdentity(user, info, userTournamentId), [user, info, userTournamentId]);

  // Сигнатура, а не сам объект: `buildAnalyticsIdentity` возвращает новый объект на каждый
  // рендер, и эффект по ссылке слал бы `identify` при любом обновлении соседнего слайса.
  const signature = identity ? JSON.stringify(identity) : null;
  const sentSignature = useRef<string | null>(null);

  useEffect(() => {
    if (!identity || !signature || sentSignature.current === signature) {
      return;
    }

    sentSignature.current = signature;
    applyIdentity(identity);
    // После `setUserId`, чтобы событие уехало уже с пользователем.
    trackApplicationOpenedOnce(identity.userId);
  }, [identity, signature]);

  const hadUser = useRef(false);

  useEffect(() => {
    if (user) {
      hadUser.current = true;
      return;
    }

    // Пользователя ещё не было (загрузка до `authCheck`, анонимный заход) — сбрасывать нечего.
    if (!hadUser.current) {
      return;
    }

    hadUser.current = false;
    sentSignature.current = null;
    amplitude.reset();
  }, [user]);
}

function applyIdentity(identity: AnalyticsIdentity): void {
  amplitude.setUserId(identity.userId);

  const identifyEvent = new amplitude.Identify();
  Object.entries(identity.userProperties).forEach(([key, value]) => {
    identifyEvent.set(key, value);
  });
  amplitude.identify(identifyEvent);

  if (identity.organizerId !== null) {
    // Группа в Amplitude — всегда строка: по ней строится разбивка отчётов по организациям.
    amplitude.setGroup('organizer', String(identity.organizerId));
  }
}
